import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Popconfirm,
  Divider,
  Row,
  Col,
  Spin,
  Result,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SwapOutlined,
  SendOutlined,
  SyncOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import {
  getInvoiceById,
  deleteInvoice,
  downloadInvoicePdf,
  sendEInvoice,
  checkEInvoiceStatus,
  cancelEInvoice,
} from "../../../services/invoiceService";
import {
  getInvoiceStatusInfo,
  getEInvoiceStatusInfo,
  getPaymentMethodLabel,
  getUnitCodeLabel,
} from "../../../constants/invoiceConstants";
import { formatPrice, formatDate } from "../../../utils/formatters";

import InvoiceStatusModal from "../../../components/modals/InvoiceStatusModal";
import UpdateInvoiceModal from "../../../components/modals/UpdateInvoiceModal";

const { Content } = Layout;
const { Title, Text } = Typography;

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await getInvoiceById(id);
      setInvoice(response.data);
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setError("Fatura bulunamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const handleDelete = async () => {
    try {
      await deleteInvoice(id);
      toast.success("Fatura silindi");
      navigate("/admin/invoices");
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await downloadInvoicePdf(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoice.invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleModalSuccess = () => {
    setShowStatusModal(false);
    setShowUpdateModal(false);
    fetchInvoice();
  };

  const handleSendEInvoice = async () => {
    try {
      await sendEInvoice(id);
      toast.success("E-fatura gönderildi");
      fetchInvoice();
    } catch (error) {
      console.error("Error sending e-invoice:", error);
    }
  };

  const handleCheckEInvoiceStatus = async () => {
    try {
      await checkEInvoiceStatus(id);
      toast.success("E-fatura durumu güncellendi");
      fetchInvoice();
    } catch (error) {
      console.error("Error checking e-invoice status:", error);
    }
  };

  const handleCancelEInvoice = async () => {
    try {
      await cancelEInvoice(id);
      toast.success("E-fatura iptal edildi");
      fetchInvoice();
    } catch (error) {
      console.error("Error cancelling e-invoice:", error);
    }
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        <Content
          style={{
            padding: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  if (error || !invoice) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        <Content style={{ padding: "24px" }}>
          <Result
            status="404"
            title="Fatura Bulunamadı"
            subTitle={error}
            extra={
              <Button type="primary" onClick={() => navigate("/admin/invoices")}>
                Faturalara Dön
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  const { color, text: statusText } = getInvoiceStatusInfo(invoice.status);
  const isDraft = invoice.status === "DRAFT";

  const itemColumns = [
    {
      title: "Açıklama",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Birim",
      dataIndex: "unitCode",
      key: "unitCode",
      align: "center",
      render: (val) => getUnitCodeLabel(val),
    },
    {
      title: "Miktar",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
    },
    {
      title: "Birim Fiyat",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (val) => formatPrice(val),
      align: "right",
    },
    {
      title: "KDV %",
      dataIndex: "taxRate",
      key: "taxRate",
      render: (val) => `%${val || 0}`,
      align: "center",
    },
    {
      title: "KDV Tutarı",
      dataIndex: "taxAmount",
      key: "taxAmount",
      render: (val) => formatPrice(val),
      align: "right",
    },
    {
      title: "Toplam",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (val) => <Text strong>{formatPrice(val)}</Text>,
      align: "right",
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 24 }}
          >
            <Col>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/admin/invoices")}
                >
                  Geri
                </Button>
                <Title level={2} style={{ margin: 0 }}>
                  Fatura #{invoice.invoiceNumber}
                </Title>
                <Tag color={color}>{statusText}</Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<SwapOutlined />}
                  onClick={() => setShowStatusModal(true)}
                >
                  Durum Değiştir
                </Button>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={handleDownloadPdf}
                >
                  PDF İndir
                </Button>
                {invoice.status === "SENT" && (!invoice.einvoiceStatus || invoice.einvoiceStatus === "NONE") && (
                  <Popconfirm
                    title="Bu faturayı e-fatura olarak göndermek istiyor musunuz?"
                    onConfirm={handleSendEInvoice}
                    okText="Evet"
                    cancelText="Hayır"
                  >
                    <Button icon={<SendOutlined />} type="primary">
                      E-Fatura Gönder
                    </Button>
                  </Popconfirm>
                )}
                {invoice.einvoiceStatus && invoice.einvoiceStatus === "SENT" && (
                  <Button icon={<SyncOutlined />} onClick={handleCheckEInvoiceStatus}>
                    Durumu Sorgula
                  </Button>
                )}
                {invoice.einvoiceStatus === "ACCEPTED" && (
                  <Popconfirm
                    title="E-faturayı iptal etmek istediğinize emin misiniz?"
                    onConfirm={handleCancelEInvoice}
                    okText="Evet"
                    cancelText="Hayır"
                  >
                    <Button danger icon={<CloseCircleOutlined />}>
                      E-Fatura İptal
                    </Button>
                  </Popconfirm>
                )}
                {isDraft && (
                  <>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setShowUpdateModal(true)}
                    >
                      Düzenle
                    </Button>
                    <Popconfirm
                      title="Bu faturayı silmek istediğinize emin misiniz?"
                      onConfirm={handleDelete}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Sil
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </motion.div>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
            >
              <Card title="Fatura Bilgileri" style={{ borderRadius: 8 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Fatura No">
                    {invoice.invoiceNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fatura Tarihi">
                    {formatDate(invoice.invoiceDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Vade Tarihi">
                    {formatDate(invoice.dueDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Durum">
                    <Tag color={color}>{statusText}</Tag>
                  </Descriptions.Item>
                  {invoice.paymentMethod && (
                    <Descriptions.Item label="Ödeme Yöntemi">
                      {getPaymentMethodLabel(invoice.paymentMethod)}
                    </Descriptions.Item>
                  )}
                  {invoice.paidAt && (
                    <Descriptions.Item label="Ödeme Tarihi">
                      {formatDate(invoice.paidAt)}
                    </Descriptions.Item>
                  )}
                  {invoice.notes && (
                    <Descriptions.Item label="Notlar">
                      {invoice.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </motion.div>
          </Col>
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
            >
              <Card title="Müşteri Bilgileri" style={{ borderRadius: 8 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Müşteri">
                    {invoice.customerName}
                  </Descriptions.Item>
                  {invoice.customerTaxNumber && (
                    <Descriptions.Item label="Vergi No">
                      {invoice.customerTaxNumber}
                    </Descriptions.Item>
                  )}
                  {invoice.customerTaxOffice && (
                    <Descriptions.Item label="Vergi Dairesi">
                      {invoice.customerTaxOffice}
                    </Descriptions.Item>
                  )}
                  {invoice.customerAddress && (
                    <Descriptions.Item label="Adres">
                      {invoice.customerAddress}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {invoice.einvoiceStatus && invoice.einvoiceStatus !== "NONE" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.4 }}
            style={{ marginTop: 16 }}
          >
            <Card title="E-Fatura Bilgileri" style={{ borderRadius: 8 }}>
              <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                <Descriptions.Item label="E-Fatura Durumu">
                  <Tag color={getEInvoiceStatusInfo(invoice.einvoiceStatus).color}>
                    {getEInvoiceStatusInfo(invoice.einvoiceStatus).text}
                  </Tag>
                </Descriptions.Item>
                {invoice.uuid && (
                  <Descriptions.Item label="UUID">{invoice.uuid}</Descriptions.Item>
                )}
                {invoice.invoiceTypeCode && (
                  <Descriptions.Item label="Fatura Tipi">
                    {invoice.invoiceTypeCode === "SATIS" ? "Satış" : "İade"}
                  </Descriptions.Item>
                )}
                {invoice.integratorId && (
                  <Descriptions.Item label="Entegratör ID">{invoice.integratorId}</Descriptions.Item>
                )}
                {invoice.sentAt && (
                  <Descriptions.Item label="Gönderim Tarihi">{formatDate(invoice.sentAt)}</Descriptions.Item>
                )}
                {invoice.gibAcceptedAt && (
                  <Descriptions.Item label="GİB Kabul Tarihi">{formatDate(invoice.gibAcceptedAt)}</Descriptions.Item>
                )}
                {invoice.gibResponseDescription && (
                  <Descriptions.Item label="GİB Yanıtı" span={3}>
                    {invoice.gibResponseDescription}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          style={{ marginTop: 16 }}
        >
          <Card title="Fatura Kalemleri" style={{ borderRadius: 8 }}>
            <Table
              columns={itemColumns}
              dataSource={invoice.items || []}
              rowKey="id"
              pagination={false}
              bordered
              scroll={{ x: true }}
            />
            <Divider />
            <Row justify="end">
              <Col>
                <Space direction="vertical" size={4} style={{ textAlign: "right" }}>
                  <Text>Ara Toplam: {formatPrice(invoice.subtotal)}</Text>
                  <Text>KDV Toplam: {formatPrice(invoice.taxAmount)}</Text>
                  <Title level={4} style={{ margin: 0 }}>
                    Genel Toplam: {formatPrice(invoice.totalAmount)}
                  </Title>
                </Space>
              </Col>
            </Row>
          </Card>
        </motion.div>

        <InvoiceStatusModal
          visible={showStatusModal}
          invoice={invoice}
          onCancel={() => setShowStatusModal(false)}
          onSuccess={handleModalSuccess}
        />
        <UpdateInvoiceModal
          visible={showUpdateModal}
          invoice={invoice}
          onCancel={() => setShowUpdateModal(false)}
          onSuccess={handleModalSuccess}
        />
      </Content>
    </Layout>
  );
};

export default InvoiceDetailPage;
